import { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Search,
    RefreshCcw,
    Loader2,
    X,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Square,
    CheckSquare,
    Monitor,
    Network,
    Globe,
    Activity,
    ShieldCheck,
    ArrowUp,
    ArrowDown,
    ArrowUpDown,
    Bell
} from 'lucide-react';
import { CustomDropdown } from '../../components/CustomDropdown ';
import baseapi from '../../services/baseapi';
import { useClientLevel } from '../../hooks/useClientLevel';
import { showError, showSuccess } from '../../utils/toast';
import ConfirmDialog from '../../components/ConfirmDialog';
import { ItemsPerPageDropdown } from '../../components/ItemsPerPageDropdown ';
import ColumnVisibilityDropdown, { type ColumnOption } from '../../components/ColumnVisibilityDropdown';

interface ServiceMonitor {
    service_name: string;
    display_name: string;
    asset_count: number;
    monitored_asset_count: number;
    monitored: boolean;
    monitor_id: number | null;
    monitor_scope: string | null;
    create_event: boolean;
    create_ticket: boolean;
}

export type ServiceMonitorColumnKey =
    | 'service_details'
    | 'asset_count'
    | 'monitored_asset_count'
    | 'monitored'
    | 'create_event'
    | 'actions';

export default function ServicesMonitoring() {
    const level = useClientLevel();
    const clientId = level?.id || level?.client_id;

    const [services, setServices] = useState<ServiceMonitor[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedServices, setSelectedServices] = useState<Set<string>>(new Set());
    const [assets, setAssets] = useState<{ label: string; value: string }[]>([]);
    const [selectedAssetId, setSelectedAssetId] = useState<string>('');
    const [selectedAgentsMap, setSelectedAgentsMap] = useState<Record<string, Set<number>>>({});
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [isUnmonitorConfirmOpen, setIsUnmonitorConfirmOpen] = useState(false);
    // const [selectedMonitorId, setSelectedMonitorId] = useState<number | null>(null);
    const [selectedSvcForUnmonitor, setSelectedSvcForUnmonitor] = useState<string | null>(null);
    const [selectedAgentsForUnmonitor, setSelectedAgentsForUnmonitor] = useState<number[]>([]);
    const [bulkUnmonitorAgents, setBulkUnmonitorAgents] = useState<Set<number>>(new Set());

    // Column visibility state
    const [visibleColumns, setVisibleColumns] = useState<Record<ServiceMonitorColumnKey, boolean>>({
        service_details: true,
        asset_count: true,
        monitored_asset_count: true,
        monitored: true,
        create_event: true,
        actions: true,
    });

    const columnOptions: ColumnOption<ServiceMonitorColumnKey>[] = useMemo(() => [
        { key: 'service_details', label: 'Service Details' },
        ...(!selectedAssetId ? [
            { key: 'asset_count' as ServiceMonitorColumnKey, label: 'Assets' },
            { key: 'monitored_asset_count' as ServiceMonitorColumnKey, label: 'Monitored' },
        ] : []),
        { key: 'monitored', label: 'Monitoring' },
        { key: 'create_event', label: 'Event' },
        { key: 'actions', label: 'Actions' },
    ], [selectedAssetId]);

    const visibleColsCount = useMemo(() => {
        let count = 1; // Checkbox column
        if (visibleColumns.service_details) count++;
        if (!selectedAssetId && visibleColumns.asset_count) count++;
        if (!selectedAssetId && visibleColumns.monitored_asset_count) count++;
        if (visibleColumns.monitored) count++;
        if (visibleColumns.create_event) count++;
        if (visibleColumns.actions) count++;
        return count;
    }, [visibleColumns, selectedAssetId]);

    // Sidebar state
    const [isAssetSidebarOpen, setIsAssetSidebarOpen] = useState(false);
    const [sidebarLoading, setSidebarLoading] = useState(false);
    const [serviceAssets, setServiceAssets] = useState<any[]>([]);
    const [selectedAssetService, setSelectedAssetService] = useState<string | null>(null);
    const [sidebarFilter, setSidebarFilter] = useState<'all' | 'monitored'>('all');

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(35); // Grid layout usually looks better with multiples of 3 or 4
    const [total, setTotal] = useState(0);

    const totalPages = Math.ceil(total / limit) || 1;
    const startIndex = (currentPage - 1) * limit;
    const endIndex = startIndex + limit;

    const handlePageChange = (page: number): void => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleItemsPerPageChange = (value: number): void => {
        setLimit(value);
        setCurrentPage(1);
    };

    const [submitting, setSubmitting] = useState(false);
    const [sortBy, setSortBy] = useState<string>('service_name');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

    const fetchServices = useCallback(async () => {
        if (!clientId) return;
        setLoading(true);
        try {
            const skip = (currentPage - 1) * limit;
            const queryObj: any = {
                client_id: clientId
            };

            if (selectedAssetId) {
                queryObj.asset_id = Number(selectedAssetId);
            }

            if (searchTerm) {
                queryObj.$or = [
                    { service_name: { "$ilike": `%${searchTerm}%` } },
                    { display_name: { "$ilike": `%${searchTerm}%` } }
                ];
            }

            const params: any = {
                query: JSON.stringify(queryObj),
                skip,
                limit,
                sort_by: sortBy,
                sort_order: sortOrder
            };



            const response = await baseapi.get('/service_monitor/unique', { params });
            if (response.data.status) {
                setServices(response.data.data || []);
                setTotal(response.data.total || 0);
            }
        } catch (error: any) {
            showError(error.message || 'Failed to fetch services');
        } finally {
            setLoading(false);
        }
    }, [clientId, currentPage, limit, searchTerm, selectedAssetId, sortBy, sortOrder]);

    const fetchAssets = useCallback(async () => {
        if (!clientId) return;
        try {
            const queryObj = {
                client_id: clientId,
                asset_type: "local_asset"
            };
            const params = {
                query: JSON.stringify(queryObj),
                limit: 500 // Fetching more assets as there might be many
            };
            const response = await baseapi.get('/assets', { params });
            if (response.data.status && response.data.data) {
                const assetOptions = response.data.data.map((a: any) => ({
                    label: a.hostname || a.ip_address || a.name || `Asset ${a.id}`,
                    value: a.id.toString(),
                    agent_id: a.agent_id
                }));
                // Add an "All Assets" option
                setAssets([{ label: 'All Assets', value: '', agent_id: null }, ...assetOptions]);
            }
        } catch (error) {
            console.error('Failed to fetch assets:', error);
        }
    }, [clientId]);

    useEffect(() => {
        fetchAssets();
    }, [fetchAssets]);

    useEffect(() => {
        fetchServices();
    }, [fetchServices]);

    const handleSort = (field: string) => {
        let nextOrder: 'asc' | 'desc' = 'asc';
        if (sortBy === field) {
            nextOrder = sortOrder === 'asc' ? 'desc' : 'asc';
        }
        setSortBy(field);
        setSortOrder(nextOrder);
        setCurrentPage(1); // Reset to first page when sorting changes
    };

    const SortIcon = ({ field }: { field: string }) => {
        if (sortBy !== field) return <ArrowUpDown size={10} className="text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" />;
        return sortOrder === 'asc'
            ? <ArrowUp size={10} className="text-orange-500" />
            : <ArrowDown size={10} className="text-orange-500" />;
    };

    useEffect(() => {
        setCurrentPage(1); // Reset to first page on search or asset change
    }, [searchTerm, selectedAssetId]);

    const toggleSelection = (svc: ServiceMonitor) => {
        // Only block selection if ALL assets are already monitored
        if (svc.monitored_asset_count === svc.asset_count && svc.asset_count > 0) return;

        const name = svc.service_name;
        const nextSelected = new Set(selectedServices);
        const nextAgentsMap = { ...selectedAgentsMap };

        if (nextSelected.has(name)) {
            nextSelected.delete(name);
            delete nextAgentsMap[name];
        } else {
            nextSelected.add(name);
            // If top-level asset dropdown has a selection, add its agent id
            if (selectedAssetId) {
                const selAsset: any = assets.find((a: any) => a.value === selectedAssetId);
                if (selAsset?.agent_id) {
                    nextAgentsMap[name] = new Set([selAsset.agent_id]);
                }
            }
        }
        setSelectedServices(nextSelected);
        setSelectedAgentsMap(nextAgentsMap);
    };

    const toggleAgentInSidebar = (svcName: string, agentId: number) => {
        if (sidebarFilter === 'monitored') {
            // Bulk unmonitor selection
            const next = new Set(bulkUnmonitorAgents);
            if (next.has(agentId)) next.delete(agentId);
            else next.add(agentId);
            setBulkUnmonitorAgents(next);
            return;
        }

        const nextAgentsMap = { ...selectedAgentsMap };
        const currentAgents = new Set(nextAgentsMap[svcName] || []);

        if (currentAgents.has(agentId)) {
            currentAgents.delete(agentId);
        } else {
            currentAgents.add(agentId);
        }

        const nextSelected = new Set(selectedServices);
        if (currentAgents.size > 0) {
            nextAgentsMap[svcName] = currentAgents;
            nextSelected.add(svcName);
        } else {
            delete nextAgentsMap[svcName];
            nextSelected.delete(svcName);
        }

        setSelectedAgentsMap(nextAgentsMap);
        setSelectedServices(nextSelected);
    };

    const handleAssetCountClick = async (svcName: string, filter: 'all' | 'monitored' = 'all') => {
        setSelectedAssetService(svcName);
        setSidebarFilter(filter);
        setBulkUnmonitorAgents(new Set()); // Clear bulk selection
        setIsAssetSidebarOpen(true);
        setSidebarLoading(true);
        try {
            const params = {
                service_name: svcName,
                client_id: level.id || clientId,
                monitored: filter === 'monitored' ? true : undefined
            };
            const response = await baseapi.get('/service_monitor/service_assets', { params });
            if (response.data.status) {
                setServiceAssets(response.data.data || []);
            }
        } catch (error: any) {
            showError(error.message || 'Failed to fetch service assets');
        } finally {
            setSidebarLoading(false);
        }
    };

    const handleSelectAll = () => {
        const selectableOnPage = services.filter(s => s.monitored_asset_count < s.asset_count).map(s => s.service_name);
        if (selectableOnPage.length === 0) return;

        const areAllSelected = selectableOnPage.every(name => selectedServices.has(name));
        const next = new Set(selectedServices);

        if (areAllSelected) {
            selectableOnPage.forEach(name => next.delete(name));
        } else {
            selectableOnPage.forEach(name => next.add(name));
        }
        setSelectedServices(next);
    };

    const handleMonitorClick = () => {
        if (selectedServices.size === 0) {
            showError('Please select at least one service to monitor');
            return;
        }
        setIsConfirmOpen(true);
    };

    const handleUnmonitorClick = (svcName: string, agentIds: number[]) => {
        setSelectedSvcForUnmonitor(svcName);
        setSelectedAgentsForUnmonitor(agentIds);
        setIsUnmonitorConfirmOpen(true);
    };

    const handleConfirmUnmonitor = async () => {
        if (!selectedSvcForUnmonitor) return;
        try {
            const payload: any = {
                service_name: selectedSvcForUnmonitor,
                client_id: level.id || clientId,
            };

            if (selectedAgentsForUnmonitor.length > 0) {
                payload.agent_ids = selectedAgentsForUnmonitor;
            }

            const response = await baseapi.post('/service_monitor/unmonitor', payload);
            if (response.data.status) {
                showSuccess('Service unmonitored successfully');
                fetchServices();
                if (isAssetSidebarOpen) {
                    // Refresh sidebar content if open
                    handleAssetCountClick(selectedAssetService!, sidebarFilter);
                }
            }
        } catch (error: any) {
            showError(error.message || 'Failed to unmonitor service');
        } finally {
            setIsUnmonitorConfirmOpen(false);
            setSelectedSvcForUnmonitor(null);
            setSelectedAgentsForUnmonitor([]);
            setBulkUnmonitorAgents(new Set()); // Added to clear bulk unmonitor selection
        }
    };

    const handleConfirmMonitor = async () => {
        setSubmitting(true);
        try {
            const payload = {
                client_id: level.id || clientId,
                services: Array.from(selectedServices).map(svcName => {
                    const agentIds = selectedAgentsMap[svcName] ? Array.from(selectedAgentsMap[svcName]) : [];
                    const svc: any = {
                        service_name: svcName,
                        create_event: true,
                        create_ticket: true,
                    };
                    if (agentIds.length > 0) {
                        svc.agent_ids = agentIds;
                    }
                    return svc;
                })
            };

            const response = await baseapi.post('/service_monitor/save', payload);

            if (response.data.status) {
                showSuccess('Service monitoring started successfully');
                setIsConfirmOpen(false);
                fetchServices(); // Refresh to show monitored status
                setSelectedServices(new Set()); // Clear selections
                setSelectedAgentsMap({}); // Added to clear agent selections
                setBulkUnmonitorAgents(new Set()); // Ensure all sidebar selections are gone
            } else {
                showError(response.data.message || 'Failed to save monitoring configuration');
            }
        } catch (error: any) {
            console.error('Submit error:', error);
            showError(error.message || 'Failed to save configuration');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className=" animate-fade-in flex flex-col h-[calc(100vh-120px)] overflow-hidden">
            <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-1.5">
                    <div className="bg-orange-600 p-1.5 rounded shadow-lg">
                        <Activity className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h1 className="font-bold text-lg text-gray-200 leading-none">Services Monitoring</h1>
                    </div>
                    {!loading && total !== undefined && (
                        <div className="font-bold text-lg text-gray-100">
                            ({total})
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => fetchServices()}
                        className="rounded-lg bg-gray-800 border border-gray-700 text-gray-200 p-2 hover:bg-gray-700 transition-colors"
                        title="Refresh"
                    >
                        <RefreshCcw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                    </button>

                    <div className="relative w-full sm:w-56">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                        <input
                            type="text"
                            placeholder="Search service names..."
                            className="w-full bg-gray-800/50 border border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-white focus:border-orange-500 outline-none transition-all"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="flex items-center">
                        <CustomDropdown
                            data={assets}
                            selectedValue={selectedAssetId}
                            onSelect={setSelectedAssetId}
                            placeholder="All Assets"
                            width="w-56"
                            buttonHeight={8}
                            className="!bg-transparent !border-none !shadow-none"
                        />
                    </div>

                    <ColumnVisibilityDropdown
                        columns={columnOptions}
                        visibleColumns={visibleColumns}
                        onChange={setVisibleColumns}
                    />

                    {selectedServices.size > 0 && (
                        <button
                            onClick={() => setSelectedServices(new Set())}
                            className="flex items-center gap-2 px-3 py-2 bg-gray-800/80 border border-gray-700 hover:border-red-500/50 text-gray-200 hover:text-red-500 rounded-lg font-bold text-[10px] uppercase tracking-wider transition-all active:scale-95 group"
                        >
                            <X size={14} className="group-hover:rotate-90 transition-transform" />
                            Deselect ({selectedServices.size})
                        </button>
                    )}

                    <button
                        onClick={handleMonitorClick}
                        className="flex items-center gap-2 px-6 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-bold text-[11px] uppercase tracking-wider transition-all shadow-lg shadow-orange-900/20 active:scale-95"
                    >
                        <Activity size={14} />
                        Monitor
                    </button>
                </div>
            </div>

            <div className="flex-1 bg-gray-800/40 border border-gray-700 rounded-t-xl overflow-hidden flex flex-col shadow-2xl backdrop-blur-md min-h-0">

                {/* Table View */}
                <div className="flex-1 overflow-auto custom-scroll">
                    <table className="w-full text-left border-collapse min-w-[800px]">
                        <thead className="bg-gray-800/80 sticky top-0 z-10">
                            <tr className="border-b border-gray-700/50">
                                <th className="px-4 py-2 w-10">
                                    <div className="cursor-pointer group flex items-center justify-center" onClick={handleSelectAll}>
                                        {services.length > 0 && services.filter(s => s.monitored_asset_count < s.asset_count).length > 0 && services.filter(s => s.monitored_asset_count < s.asset_count).every(s => selectedServices.has(s.service_name)) ? (
                                            <CheckSquare size={16} className="text-orange-500 fill-orange-500/10" />
                                        ) : (
                                            <Square size={16} className="text-gray-700 group-hover:text-gray-200" />
                                        )}
                                    </div>
                                </th>
                                {visibleColumns.service_details && (
                                    <th className="px-4 py-2">
                                        <div
                                            className="flex items-center gap-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest cursor-pointer group hover:text-orange-400 transition-colors"
                                            onClick={() => handleSort('display_name')}
                                        >
                                            <Monitor size={14} className="text-cyan-400" />
                                            <span>Service Details</span>
                                            <SortIcon field="display_name" />
                                        </div>
                                    </th>
                                )}
                                {!selectedAssetId && (
                                    <>
                                        {visibleColumns.asset_count && (
                                            <th className="px-4 py-2">
                                                <div
                                                    className="flex items-center justify-center gap-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest cursor-pointer group hover:text-orange-400 transition-colors"
                                                    onClick={() => handleSort('asset_count')}
                                                >
                                                    <Network size={14} className="text-indigo-400" />
                                                    <span>Assets</span>
                                                    <SortIcon field="asset_count" />
                                                </div>
                                            </th>
                                        )}
                                        {visibleColumns.monitored_asset_count && (
                                            <th className="px-4 py-2">
                                                <div
                                                    className="flex items-center justify-center gap-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest cursor-pointer group hover:text-orange-400 transition-colors"
                                                    onClick={() => handleSort('monitored_asset_count')}
                                                >
                                                    <ShieldCheck size={14} className="text-emerald-400" />
                                                    <span>Monitored</span>
                                                    <SortIcon field="monitored_asset_count" />
                                                </div>
                                            </th>
                                        )}
                                    </>
                                )}
                                {visibleColumns.monitored && (
                                    <th className="px-4 py-2">
                                        <div
                                            className="flex items-center justify-center gap-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest cursor-pointer group hover:text-orange-400 transition-colors"
                                            onClick={() => handleSort('monitored')}
                                        >
                                            <Activity size={14} className="text-orange-400" />
                                            <span>Monitoring</span>
                                            <SortIcon field="monitored" />
                                        </div>
                                    </th>
                                )}
                                {visibleColumns.create_event && (
                                    <th className="px-4 py-2">
                                        <div
                                            className="flex items-center justify-center gap-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest cursor-pointer group hover:text-orange-400 transition-colors"
                                            onClick={() => handleSort('create_event')}
                                        >
                                            <Bell size={14} className="text-purple-400" />
                                            <span>Event</span>
                                            <SortIcon field="create_event" />
                                        </div>
                                    </th>
                                )}
                                {visibleColumns.actions && (
                                    <th className="px-4 py-2 text-[10px] font-bold text-gray-200 uppercase tracking-widest text-center">Actions</th>
                                )}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700/60 bg-gray-900/20 text-xs">
                            {loading ? (
                                <tr>
                                    <td colSpan={visibleColsCount} className="px-4 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center">
                                            <Loader2 className="w-6 h-6 animate-spin text-orange-500 mb-2" />
                                            <p className="text-gray-200 font-bold uppercase tracking-widest text-[9px]">Fetching services...</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : services.length === 0 ? (
                                <tr>
                                    <td colSpan={visibleColsCount} className="px-4 py-12 text-center text-gray-200 italic text-xs">
                                        No services found matching your criteria.
                                    </td>
                                </tr>
                            ) : (
                                services.map((svc) => (
                                    <tr
                                        key={svc.service_name}
                                        onClick={() => toggleSelection(svc)}
                                        className={`hover:bg-gray-800/40 transition-colors group ${svc.monitored_asset_count === svc.asset_count && svc.asset_count > 0 ? 'cursor-not-allowed' : 'cursor-pointer'} ${selectedServices.has(svc.service_name) ? 'bg-orange-500/5' : ''}`}
                                    >
                                        <td className="px-4 py-2 w-10 text-center">
                                            <div className="flex items-center justify-center">
                                                {svc.monitored_asset_count === svc.asset_count && svc.asset_count > 0 ? (
                                                    <CheckSquare size={16} className="text-gray-700 fill-gray-700/10 cursor-not-allowed" />
                                                ) : selectedServices.has(svc.service_name) ? (
                                                    <CheckSquare size={16} className="text-orange-400 fill-orange-500/10" />
                                                ) : (
                                                    <Square size={16} className="text-gray-700 group-hover:text-gray-200" />
                                                )}
                                            </div>
                                        </td>
                                        {visibleColumns.service_details && (
                                            <td className="px-4 py-2">
                                                <div className="flex flex-col">
                                                    <span className="text-[11px] font-bold text-gray-200 group-hover:text-orange-500 transition-colors">
                                                        {svc.display_name}
                                                    </span>
                                                    <span className="text-[9px] text-gray-200 font-mono">
                                                        {svc.service_name}
                                                    </span>
                                                </div>
                                            </td>
                                        )}
                                        {!selectedAssetId && (
                                            <>
                                                {visibleColumns.asset_count && (
                                                    <td className="px-4 py-2">
                                                        <div className="flex justify-center">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleAssetCountClick(svc.service_name, 'all');
                                                                }}
                                                                className="w-7 h-7 rounded-full bg-blue-500/10 border border-blue-500/40 text-blue-400 text-[10px] font-bold flex items-center justify-center hover:bg-blue-500/20 transition-all hover:scale-105"
                                                            >
                                                                {svc.asset_count}
                                                            </button>
                                                        </div>
                                                    </td>
                                                )}
                                                {visibleColumns.monitored_asset_count && (
                                                    <td className="px-4 py-2">
                                                        <div className="flex justify-center">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleAssetCountClick(svc.service_name, 'monitored');
                                                                }}
                                                                className={`w-7 h-7 rounded-full bg-emerald-500/10 border border-emerald-500/40 text-emerald-400 text-[10px] font-bold flex items-center justify-center hover:bg-emerald-500/20 transition-all hover:scale-105 ${svc.monitored_asset_count === 0 ? 'opacity-30 cursor-not-allowed grayscale' : ''}`}
                                                                disabled={svc.monitored_asset_count === 0}
                                                            >
                                                                {svc.monitored_asset_count}
                                                            </button>
                                                        </div>
                                                    </td>
                                                )}
                                            </>
                                        )}
                                        {visibleColumns.monitored && (
                                            <td className="px-4 py-2 text-center">
                                                <div className="flex justify-center">
                                                    {svc.monitored ? (
                                                        <div className="flex items-center gap-1 text-emerald-500 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20 text-[9px] font-bold uppercase tracking-wider">
                                                            <Activity size={10} /> Active
                                                        </div>
                                                    ) : (
                                                        <div className="text-gray-200 px-2 py-0.5 rounded-full border border-gray-800 text-[9px] font-bold uppercase tracking-wider">
                                                            Inactive
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                        {visibleColumns.create_event && (
                                            <td className="px-4 py-2 text-center">
                                                <div className="flex justify-center">
                                                    <div className={`w-2 h-2 rounded-full ${svc.create_event ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.4)]' : 'bg-gray-800'}`} />
                                                </div>
                                            </td>
                                        )}
                                        {visibleColumns.actions && (
                                            <td className="px-4 py-2">
                                                <div className="flex justify-center">
                                                    {svc.monitored ? (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                const agentIds: number[] = [];
                                                                if (selectedAssetId) {
                                                                    const selAsset: any = assets.find((a: any) => a.value === selectedAssetId);
                                                                    if (selAsset?.agent_id) {
                                                                        agentIds.push(selAsset.agent_id);
                                                                    }
                                                                }
                                                                handleUnmonitorClick(svc.service_name, agentIds);
                                                            }}
                                                            className="text-[9px] font-bold uppercase tracking-widest text-red-500 hover:text-red-400 border border-red-900/50 px-2 py-1 rounded backdrop-blur-sm bg-red-500/5 hover:bg-red-500/10 transition-all active:scale-95"
                                                        >
                                                            Unmonitor
                                                        </button>
                                                    ) : (
                                                        <div className="text-[9px] font-bold uppercase tracking-widest text-gray-700 px-2 py-1">—</div>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Table Footer */}
            <div className="bg-gray-800/50 border rounded-b-xl border-gray-700 px-2 py-1 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    {/* <select
                        className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-[10px] focus:outline-none  "
                        value={limit}
                        onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                    >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={35}>35</option>
                        <option value={50}>50</option>
                    </select> */}
                    <ItemsPerPageDropdown
                        value={limit}
                        onChange={handleItemsPerPageChange}
                    />
                    <span className="text-[10px] text-gray-200 font-bold uppercase tracking-widest">
                        <span className="text-gray-200">{startIndex + 1}</span> - <span className="text-gray-200">{Math.min(endIndex, total)}</span> OF {total}
                    </span>
                </div>
                <div className="flex items-center gap-1">
                    <button onClick={() => handlePageChange(1)} disabled={currentPage === 1 || loading} className="p-1.5 text-gray-500 hover:text-white disabled:opacity-30"><ChevronsLeft className="w-4 h-4" /></button>
                    <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1 || loading} className="p-1.5 text-gray-500 hover:text-white disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
                    <div className="px-3 py-1 bg-orange-600 rounded-lg text-white text-[11px] font-bold mx-1.5 shadow-lg shadow-orange-900/20">{currentPage}</div>
                    <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || total === 0 || loading} className="p-1.5 text-gray-500 hover:text-white disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
                    <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages || total === 0 || loading} className="p-1.5 text-gray-500 hover:text-white disabled:opacity-30"><ChevronsRight className="w-4 h-4" /></button>
                </div>
            </div>

            {/* Asset Sidebar */}
            <div
                className={`fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 z-[100] ${isAssetSidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                onClick={() => setIsAssetSidebarOpen(false)}
            />

            <div className={`fixed top-0 right-0 h-full w-full sm:w-[380px] bg-gray-900 shadow-3xl border-l border-gray-800 transition-transform duration-500 ease-in-out transform z-[101] flex flex-col ${isAssetSidebarOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                {/* Sidebar Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800/60 bg-gray-900/40 backdrop-blur-md">
                    <div className="flex items-center justify-between mb-4 w-full">
                        <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${sidebarFilter === 'monitored' ? 'bg-emerald-600/20 border-emerald-500/30' : 'bg-orange-600/20 border-orange-500/30'} border shadow-inner`}>
                                {sidebarFilter === 'monitored' ? <ShieldCheck size={18} className="text-emerald-500" /> : <Monitor size={18} className="text-orange-500" />}
                            </div>
                            <div>
                                <h3 className="text-sm font-bold text-white tracking-tight leading-none uppercase">
                                    {sidebarFilter === 'monitored' ? 'Monitored Assets' : 'Associated Assets'}
                                </h3>
                                <p className="text-[10px] text-gray-500 mt-1 font-mono uppercase tracking-wider">
                                    {selectedAssetService}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => setIsAssetSidebarOpen(false)}
                            className="p-1.5 hover:bg-gray-800 rounded-md transition-colors text-gray-400"
                        >
                            <X size={16} />
                        </button>
                    </div>
                </div>

                {/* Sidebar Content */}
                <div className="flex-1 overflow-y-auto p-3 custom-scroll bg-gray-900">
                    {sidebarLoading ? (
                        <div className="flex flex-col items-center justify-center h-full">
                            <Loader2 className="w-6 h-6 animate-spin text-orange-500 mb-3" />
                            <p className="text-gray-200 font-bold uppercase tracking-widest text-[9px]">Fetching details...</p>
                        </div>
                    ) : serviceAssets.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-center">
                            <div className="p-4 bg-gray-800/40 rounded-full mb-3 border border-gray-700/30">
                                <Monitor className="text-gray-200" size={32} />
                            </div>
                            <h3 className="text-gray-200 font-bold text-sm">No Assets Found</h3>
                            <p className="text-gray-200 text-xs mt-2 max-w-xs mx-auto leading-relaxed">No assets are currently reported as running this service for this client.</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {serviceAssets.map((asset) => {
                                const isSelected = sidebarFilter === 'monitored'
                                    ? bulkUnmonitorAgents.has(asset.agent_id)
                                    : selectedAgentsMap[selectedAssetService!]?.has(asset.agent_id);

                                const canInteract = sidebarFilter === 'monitored'
                                    ? asset.monitored
                                    : !asset.monitored;

                                return (
                                    <div
                                        key={asset.asset_id}
                                        onClick={() => {
                                            if (!canInteract) return;
                                            toggleAgentInSidebar(selectedAssetService!, asset.agent_id);
                                        }}
                                        className={`p-3 border rounded-lg transition-all group flex items-center justify-between ${!canInteract
                                            ? 'bg-gray-800/10 border-gray-800/20 cursor-not-allowed opacity-50'
                                            : isSelected
                                                ? 'bg-orange-500/10 border-orange-500/50 hover:bg-orange-500/15 cursor-pointer shadow-lg'
                                                : 'bg-gray-900/60 border-gray-800/50 hover:border-orange-500/30 hover:bg-gray-800/20 cursor-pointer'
                                            }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            {/* Checkbox */}
                                            <div className="flex items-center justify-center shrink-0">
                                                {!canInteract ? (
                                                    <Square size={16} className="text-gray-800" />
                                                ) : isSelected ? (
                                                    <CheckSquare size={16} className="text-orange-400 fill-orange-500/10" />
                                                ) : (
                                                    <Square size={16} className="text-gray-700 group-hover:text-gray-200" />
                                                )}
                                            </div>

                                            <div className="w-8 h-8 rounded bg-gray-800 border border-gray-700/30 flex items-center justify-center shadow-inner shrink-0">
                                                {asset.platform?.toLowerCase() === 'windows' || asset.os_name?.toLowerCase().includes('windows') ? (
                                                    <img src="/windows.png" className="w-4 h-4 opacity-80" alt="Windows" />
                                                ) : asset.os_name?.toLowerCase().includes('ubuntu') ? (
                                                    <img src="/ubuntu.png" className="w-4 h-4 opacity-80" alt="Ubuntu" />
                                                ) : asset.platform?.toLowerCase() === 'linux' ? (
                                                    <img src="/linux.svg" className="w-4 h-4 opacity-80" alt="Linux" />
                                                ) : (
                                                    <Monitor className="w-4 h-4 text-gray-200" />
                                                )}
                                            </div>
                                            <div className="flex flex-col">
                                                <h4 className={`text-xs font-bold transition-colors ${isSelected ? 'text-orange-400' : 'text-gray-200 group-hover:text-orange-400'}`}>
                                                    {asset.hostname}
                                                </h4>
                                                <div className="flex items-center gap-2 mt-0.5 text-gray-500">
                                                    <Network size={10} className="text-orange-500/50" />
                                                    <span className="text-[10px] font-mono">{asset.ip_address}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-right flex flex-col items-end gap-1">
                                            {asset.monitored ? (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleUnmonitorClick(selectedAssetService!, [asset.agent_id]);
                                                    }}
                                                    className="text-[8px] font-bold uppercase tracking-tighter text-red-500 hover:text-red-400 border border-red-900/30 px-1.5 py-0.5 rounded bg-red-500/5 transition-all"
                                                >
                                                    Unmonitor
                                                </button>
                                            ) : (
                                                <span className="text-[8px] font-bold uppercase tracking-tight text-gray-200 bg-gray-800/50 px-1.5 py-0.5 rounded">
                                                    ID: {asset.asset_id}
                                                </span>
                                            )}
                                            <div className="flex items-center gap-1 text-[9px] font-medium text-gray-500">
                                                <Globe size={9} className="text-emerald-500/50" />
                                                {asset.agent_id}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Sidebar Footer - Actions */}
                <div className="p-4 border-t border-gray-800 bg-gray-900/80 backdrop-blur-md">
                    {sidebarFilter === 'monitored' ? (
                        <button
                            disabled={bulkUnmonitorAgents.size === 0}
                            onClick={() => handleUnmonitorClick(selectedAssetService!, Array.from(bulkUnmonitorAgents))}
                            className="w-full py-3 bg-red-600 hover:bg-red-500 disabled:bg-gray-800 disabled:text-gray-600 rounded-xl text-xs font-bold uppercase tracking-widest text-white transition-all shadow-xl shadow-red-900/20 active:scale-[0.98] flex items-center justify-center gap-2"
                        >
                            <X size={16} />
                            Stop Monitoring {bulkUnmonitorAgents.size > 0 && `(${bulkUnmonitorAgents.size})`}
                        </button>
                    ) : (
                        <button
                            onClick={() => setIsAssetSidebarOpen(false)}
                            className="w-full py-3 bg-gray-800 hover:bg-gray-700 rounded-xl text-xs font-bold uppercase tracking-widest text-gray-200 transition-all border border-gray-700 active:scale-[0.98]"
                        >
                            Close Details
                        </button>
                    )}
                </div>
            </div>

            {/* Confirmation Dialog for Monitoring */}
            <ConfirmDialog
                isOpen={isConfirmOpen}
                onClose={() => setIsConfirmOpen(false)}
                onConfirm={handleConfirmMonitor}
                isLoading={submitting}
                title="Start Service Monitoring"
                message={`Are you sure you want to start monitoring ${selectedServices.size} selected services? This will enable Event and Ticket creation by default.`}
                confirmText="Start Monitoring"
                variant="success"
            />

            {/* Confirmation Dialog for Unmonitoring */}
            <ConfirmDialog
                isOpen={isUnmonitorConfirmOpen}
                onClose={() => setIsUnmonitorConfirmOpen(false)}
                onConfirm={handleConfirmUnmonitor}
                title="Stop Service Monitoring?"
                message="Are you sure you want to stop monitoring this service? This will disable automated event and ticket generation for it."
                confirmText="Unmonitor"
                variant="danger"
            />
        </div>
    );
}
